import React from 'react';
import { Button, Divider, Switch, useTheme } from '@mui/material';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Dialog, { DialogProps } from '../../common/components/dialog/Dialog';
import TimePicker from '../../common/components/form/TimePicker';
import XCloseIcon from '../../common/icon/CloseIcon';
import { px2rem } from '../../core';
import { DayOfWeek } from './OpeningHoursSetting';
import { OpeningHour } from '@mnz/builder';

interface EditOpeningHoursModalProps extends DialogProps {
    selectedOpeningHours: DayOfWeek | null;
    onUpdate: (data: OpeningHour) => void;
}

const EditOpeningHoursModal: React.FC<EditOpeningHoursModalProps> = ({
    selectedOpeningHours,
    onUpdate,
    onClose,
    ...props
}) => {
    const theme = useTheme();
    const handleChangeStartTime = (idx: number, time: number) => {
        onUpdate({
            ...selectedOpeningHours.openingHours,
            times: selectedOpeningHours.openingHours.times.map((t, i) => {
                if (i === idx) {
                    return {
                        start: time,
                        end: t.end,
                    };
                } else {
                    return t;
                }
            }),
        });
    };
    const handleChangeEndTime = (idx: number, time: number) => {
        onUpdate({
            ...selectedOpeningHours.openingHours,
            times: selectedOpeningHours.openingHours.times.map((t, i) => {
                if (i === idx) {
                    return {
                        start: t.start,
                        end: time,
                    };
                } else {
                    return t;
                }
            }),
        });
    };
    const onRemove = (idx: number) => {
        onUpdate({
            ...selectedOpeningHours.openingHours,
            times: selectedOpeningHours.openingHours.times.filter((_, i) => i !== idx),
        });
    };
    const handleClickAddHour = () => {
        onUpdate({
            ...selectedOpeningHours.openingHours,
            times: [...selectedOpeningHours.openingHours.times, { start: 540, end: 1020 }],
        });
    };
    const handleSwitch = (allDay: boolean) => {
        onUpdate({
            ...selectedOpeningHours.openingHours,
            allDay,
        });
    };
    return (
        <Dialog onClose={onClose} anchor="bottom" title="Edit Opening Hours" showClose={true} {...props}>
            <div
                css={{
                    marginTop: px2rem(20),
                    padding: '0px 28px 20px 28px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowX: 'scroll',
                }}
            >
                {selectedOpeningHours && (
                    <>
                        <div
                            css={{
                                fontWeight: 700,
                                lineHeight: px2rem(20),
                                marginBottom: selectedOpeningHours.openingHours.times.length === 0 ? 16 : 0,
                            }}
                        >
                            {selectedOpeningHours.label}
                        </div>
                        <div
                            css={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            {selectedOpeningHours.openingHours.times.map((time, idx) => (
                                <div
                                    key={idx}
                                    css={{
                                        paddingTop: px2rem(16),
                                        paddingBottom: px2rem(16),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        borderBottom:
                                            idx < selectedOpeningHours.openingHours.times.length - 1
                                                ? `solid 1px ${theme.palette.primary40}`
                                                : 0,
                                    }}
                                >
                                    <div
                                        css={{
                                            flex: '1 1 auto',
                                        }}
                                    >
                                        <TimePicker
                                            value={time.start}
                                            onChange={(value: number) => handleChangeStartTime(idx, value)}
                                            disabled={selectedOpeningHours.openingHours.allDay}
                                        />
                                    </div>
                                    <p
                                        css={{
                                            margin: '0px 10px',
                                            flexShrink: 0,
                                        }}
                                    >
                                        to
                                    </p>
                                    <div
                                        css={{
                                            flex: '1 1 auto',
                                        }}
                                    >
                                        <TimePicker
                                            value={time.end}
                                            onChange={(value: number) => handleChangeEndTime(idx, value)}
                                            disabled={selectedOpeningHours.openingHours.allDay}
                                        />
                                    </div>
                                    <div
                                        css={{
                                            fontSize: px2rem(16),
                                            color: theme.palette.primary50,
                                            marginLeft: px2rem(10),
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => onRemove(idx)}
                                    >
                                        <XCloseIcon />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div
                            css={{
                                marginTop: px2rem(6),
                                marginBottom: px2rem(20),
                            }}
                        >
                            <Button variant="dashed" color="success" fullWidth onClick={handleClickAddHour}>
                                <FontAwesomeIcon icon={faPlus} />{' '}
                                <span css={{ paddingLeft: px2rem(10), paddingRight: px2rem(10) }}>Add Hours</span>
                            </Button>
                        </div>
                        <Divider />
                        <div
                            css={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                marginTop: px2rem(20),
                            }}
                        >
                            <div css={{ flex: '1 1 auto' }}>Open 24 Hours</div>
                            <Switch
                                checked={selectedOpeningHours.openingHours.allDay}
                                onChange={(_, value) => handleSwitch(value)}
                            />
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
};

export default EditOpeningHoursModal;
